import { Component, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { faCheck, faTimes, faCartArrowDown, faPrint } from '@fortawesome/free-solid-svg-icons';
import { map } from 'rxjs/operators';
import { FilterTypeEnum } from '../../_common/enums/filter.enum';
import { ShopListResponse, ShopModel } from './shop.model';
import { ShopService } from './shop.service';
import { AddOrEditShopDialogData, ShopAddOrEditDialog } from './add-or-edit-dialog/shop-add-or-edit.dialog';
import { MatDialog } from '@angular/material/dialog';
import { NavigationExtras, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { PAGE_SIZE_DEFAULT, PAGE_SIZE_OPTIONS } from 'src/app/_common/constants';
import { BrandService } from '../brands/brand.service';
import { PermissionEnum } from '../users/user-permission.enum';
import { AuthService } from 'src/app/auth/auth.service';
import { TableFiltersComponent } from 'src/app/_common/components/filters/table-filters/table-filters.component';

@Component({
  selector: 'app-shop',
  templateUrl: './shop.component.html',
  styleUrls: ['./shop.component.scss']
})
export class ShopComponent implements OnInit, OnDestroy {

  faTrue = faCheck;
  faFalse = faTimes;
  faShopEdit = faCartArrowDown;
  faPrinters = faPrint;

  shops!: MatTableDataSource<ShopModel>;

  pageEvent!: PageEvent;

  pageIndex: number = 0;
  pageSize: number = PAGE_SIZE_DEFAULT;
  pageSizeOptions: number[] = PAGE_SIZE_OPTIONS;

  sortDirection: SortDirection = 'asc';
  sortColumn: string = 'shopNumber';

  items: ShopModel[] = [];
  totalRows!: number;


  brands = [];

  columns: any[] = [
    {
      name: 'shopNumber',
      filter: {
        type: FilterTypeEnum.Text,
        name: 'filter-shopNumber'
      }
    },
    {
      name: 'shopName',
      filter: {
        type: FilterTypeEnum.Text,
        name: 'filter-shopName'
      }
    },
    {
      name: 'street',
      filter: {
        type: FilterTypeEnum.Text,
        name: 'filter-street'
      }
    },
    {
      name: 'postcode',
      filter: {
        type: FilterTypeEnum.Text,
        name: 'filter-postcode'
      }
    },
    {
      name: 'city',
      filter: {
        type: FilterTypeEnum.Text,
        name: 'filter-city'
      }
    },
    {
      name: 'phoneNumber',
      filter: {
        type: FilterTypeEnum.Text,
        name: 'filter-phoneNumber'
      }
    },
    {
      name: 'emailAddress',
      filter: {
        type: FilterTypeEnum.Text,
        name: 'filter-emailAddress'
      }
    },
    {
      name: 'actions',
      filter: {
        type: FilterTypeEnum.None,
        name: 'filter-actions',
        options: null
      }
    }
  ];

  displayedColumns: any[] = this.columns.map(x => x.name);
  filterColumns: any[] = this.columns.map(x => x.filter).map(x => x.name);
  filter: any = {};
  filterTypes: typeof FilterTypeEnum = FilterTypeEnum;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChildren('filterRefs') filterRefs!: QueryList<TableFiltersComponent>;

  addOrEditShopDialogData: AddOrEditShopDialogData = {
    isNewShop: false,
    shopModel: { id: null, brandId: null, brandName: null, shopName: null, shopNumber: null, street: null, city: null, postcode: null, phoneNumber: null, emailAddress: null }
  }

  subscription: Subscription;
  permissionEnum = PermissionEnum;

  constructor(private shopService: ShopService,
    public dialog: MatDialog,
    public router: Router,
    public brandService: BrandService,
    public authService: AuthService
  ) {
    this.subscription = this.shopService.componentMethodCalled$.subscribe(() => {
      this.addOrEditShopOpenDialog(null);
    });
  }

  ngOnInit(): void {
    if (this.brandService.brandList.model.length > 1) {
      this.columns.unshift({
        name: 'brands',
        filter: {
          type: FilterTypeEnum.MultipleSelect,
          name: 'filter-brands'
        }
      })
      this.displayedColumns = this.columns.map(x => x.name);
      this.filterColumns = this.columns.map(x => x.filter).map(x => x.name);
    }
    this.getBrandList();
    this.loadData();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  onPaginateChange(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadData();
  }

  onSortData(event: Sort) {
    this.sortDirection = event.direction;
    this.sortColumn = event.active;
    this.pageIndex = 0;
    this.loadData();
  }

  loadData() {
    this.shopService
      .getShops(this.pageIndex, this.pageSize, this.sortColumn, this.sortDirection, this.filter)
      .pipe(map((res: ShopListResponse) => {
        this.items = res.model;
        this.totalRows = res.totalRows;
      }))
      .subscribe();
  }

  filterData(filter: any) {
    this.filter = filter;
    this.pageIndex = 0;
    this.loadData();
  }

  addOrEditShopOpenDialog(id: number) {
    if (id) {
      this.addOrEditShopDialogData.isNewShop = false;
      this.addOrEditShopDialogData.shopModel = this.items.filter(x => x.id == id)[0];
    }
    else {
      this.addOrEditShopDialogData.isNewShop = true;
    }

    let dialogRef = this.dialog.open(ShopAddOrEditDialog, {
      width: '500px',
      data: this.addOrEditShopDialogData,
      disableClose: true,
      autoFocus: false
    });

    dialogRef.afterClosed().subscribe((data: AddOrEditShopDialogData) => {
      if (data) {
        this.shopService.saveShop(data.shopModel).subscribe((response) => {
          if (response) {
            setTimeout(() => { this.loadData(); });
          }
        });
      }
    });
  }

  onPrintersInShop(shop: ShopModel) {
    const navigationExtras: NavigationExtras = {
      state: {
        data: shop
      }
    };
    this.router.navigateByUrl('/administration/printers', { state: { shop: JSON.stringify(shop) } });
  }

  getBrandList() {
    this.brandService.getBrandList().then(() => {
      const brandList = this.brandService.brandList.model;

      if (brandList.length) {
        this.brands = brandList.map(item => ({
          value: item.id,
          name: `<div class="brand"><img class="logo" src="data:image/png;base64,${item.logo}"/><span>${item.name}</span></div>`
        }));

        const brandColumn = this.columns.find(column => column.name === "brands");

        if (!brandColumn) {
          return;
        }

        brandColumn.filter.options = [...this.brands];        
        const brandFilter = this.filterRefs.find(f => f.column?.name === "brands");
        
        const allValues = this.brands.map(b => b.value);
        brandFilter.multipleSelectFormControl.setValue(allValues);
        this.filter["brands"] = allValues;
      }
    })
  }
}
